// covered-by: frontends/carrie/src/routes/tickets/__tests__/ticketCommentPaths.test.ts
// eslint-disable-next-line import/named
import { createRoute, redirect } from "@tanstack/react-router";

import ErrorComponent from "./ticket/components/ErrorComponent";
import { query, ticketRoute } from "./ticket/route";
import TicketView from "./ticket/view";

import PendingComponent from "../../../components/PendingComponent";
import { SIDEBAR_STORAGE_NAME } from "../../../components/sidebar/SidebarProvider";
import { getFolderBreadcrumbs } from "../../../services/folders";
import { fetchTicketComments, getProject } from "../../../services/projects";
import { isPunttGuest } from "../../../utils/auth";
import { isNil } from "../../../utils/fp";
import { waitForState } from "../../../utils/hooks";
import { setBreadcrumbs } from "../../../utils/slices/punttProjects";
import {
  setActiveCommentId,
  setComments,
  setHighlightedCommentId,
  setLoading,
  setSelectedVersionIndex,
  setTicket,
} from "../../../utils/slices/ticket";
import { setDrawerOpen } from "../../../utils/slices/ui";
import { store } from "../../../utils/store";
import { authLayoutRoute } from "../../auth-layout/route";
import {
  boardIdToVersionIndex,
  parseTicketCommentParams,
} from "../ticketCommentPaths";
import {
  isTicketShellHydrated,
  onTicketShellLeave,
} from "../ticketShellLifecycle";

const ticketShellRouteOptions = {
  gcTime: 0,
  shouldReload: false,
  pendingMs: 0,
  pendingMinMs: 500,
} as const;

/** Path-based comment deep link: /tickets/:ticketId/:boardId/:commentId */
export const ticketCommentRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "tickets/$ticketId/$boardId/$commentId",
  async loader({ params, context }) {
    const parsed = parseTicketCommentParams(params);
    if (!parsed) {
      throw redirect({
        to: ticketRoute.to,
        params: { ticketId: params.ticketId },
        replace: true,
      });
    }

    const { ticketId, boardId, commentId } = parsed;
    const { queryClient } = context;

    if (ticketId === "editabledoc" || ticketId === "editabledoc2") {
      return {};
    }

    // Soft enter from /view (in-app comment click): do not flip loading — that
    // remounts PendingComponent + tldraw and was half of the click crash.
    const softShell = isTicketShellHydrated(ticketId);
    if (!softShell) {
      store.dispatch(setLoading(true));
    }

    const [ticket, comments] = await Promise.all([
      queryClient.fetchQuery({
        queryKey: ["tickets", ticketId],
        queryFn: () => getProject(ticketId),
        staleTime: 1_000,
      }),
      queryClient.fetchQuery({
        queryKey: ["ticket-comments", ticketId, query],
        queryFn: () => fetchTicketComments({ ticketId, query }),
        staleTime: 1_000,
      }),
    ]);

    if (!softShell && ticket.folder) {
      let breadcrumbs: Awaited<ReturnType<typeof getFolderBreadcrumbs>>;
      try {
        breadcrumbs = await queryClient.fetchQuery({
          queryKey: ["breadcrumbs", ticket.folder],
          queryFn: () => getFolderBreadcrumbs(ticket.folder as string),
        });
      } catch (error) {
        if (error instanceof Response && error.status === 401) {
          breadcrumbs = [];
        } else {
          throw error;
        }
      }
      store.dispatch(setBreadcrumbs(breadcrumbs));
    }

    if (!softShell) {
      store.dispatch(setTicket(ticket));
    }
    store.dispatch(setComments(comments));

    const versionIndex = boardIdToVersionIndex(
      ticket.revisionBoards ?? [],
      boardId,
    );
    store.dispatch(setSelectedVersionIndex(versionIndex));

    const commentExists = comments.some((c) => c._id === commentId);
    if (commentExists) {
      store.dispatch(setActiveCommentId(commentId));
      store.dispatch(setHighlightedCommentId(commentId));
    }

    if (!softShell) {
      await waitForState(store, (state) => !isNil(state.ticket.value));
      store.dispatch(setLoading(false));
    }

    return { commentExists };
  },
  onLeave({ params, context }) {
    const { queryClient } = context;
    const { ticketId } = params;

    // Soft leave when navigating back to /tickets/:id/view for the same ticket.
    onTicketShellLeave({ ticketId, queryClient });
  },
  onEnter() {
    localStorage.setItem(`app_${SIDEBAR_STORAGE_NAME}`, "false");
    store.dispatch(setDrawerOpen({ app: false, comments: true }));
  },
  component: TicketView,
  pendingComponent: PendingComponent,
  errorComponent: ErrorComponent,
  ...ticketShellRouteOptions,
});
